<template>
    <div class="px-2 py-4 xl:px-10">
        <div class="max-w-8xl mx-auto w-full space-y-4">
            <!-- Page Header -->
            <div class="flex items-center justify-between">
                <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                    Class Subjects Management
                </h1>
                <button
                    @click="openModal"
                    class="inline-flex items-center px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                    <Plus class="w-4 h-4 mr-1" />
                    Add Class Subject
                </button>
            </div>

            <!-- Search and Filters -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
                <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <!-- Search -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Search
                        </label>
                        <input
                            v-model="searchQuery"
                            @input="debouncedSearch"
                            type="text"
                            placeholder="Search by class, subject, teacher, or schedule..."
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    <!-- Class Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.class_id"
                            :options="classOptions"
                            label="Class"
                            placeholder="All Classes"
                            :option-label="getClassLabel"
                            option-value="id"
                            @update:model-value="fetchClassSubjects"
                        />
                    </div>

                    <!-- Subject Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.subject_id"
                            :options="subjectOptions"
                            label="Subject"
                            placeholder="All Subjects"
                            :option-label="getSubjectLabel"
                            option-value="id"
                            @update:model-value="fetchClassSubjects"
                        />
                    </div>

                    <!-- Teacher Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.teacher_id"
                            :options="teacherOptions"
                            label="Teacher"
                            placeholder="All Teachers"
                            :option-label="getTeacherName"
                            option-value="id"
                            @update:model-value="fetchClassSubjects"
                        />
                    </div>

                    <!-- Semester Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.semester_id"
                            :options="semesterOptions"
                            label="Semester"
                            placeholder="All Semesters"
                            :option-label="getSemesterLabel"
                            option-value="id"
                            @update:model-value="fetchClassSubjects"
                        />
                    </div>

                    <!-- Status Filter -->
                    <StatusFilter
                        v-model="filters.is_active"
                        @change="fetchClassSubjects"
                    />
                    <!-- Page Length Selector -->
                    <PageLengthSelector
                        v-model="classSubjectStore.perPage"
                        @update:model-value="handlePerPageChange"
                    />
                </div>
                <div class="flex justify-between items-center mt-4">
                    <button
                        @click="resetFilters()"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    >
                        <RotateCcw class="w-4 h-4 mr-1" />
                        Reset Filters
                    </button>
                </div>
            </div>

            <!-- Loading State -->
            <div
                v-if="classSubjectStore.loading"
                class="flex justify-center items-center py-12"
            >
                <div
                    class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
                ></div>
            </div>

            <!-- Error State -->
            <div
                v-if="classSubjectStore.error"
                class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
            >
                <div class="flex">
                    <div class="flex-shrink-0">
                        <div class="h-5 w-5 text-red-400">⚠️</div>
                    </div>
                    <div class="ml-3">
                        <h3
                            class="text-sm font-medium text-red-800 dark:text-red-200"
                        >
                            Error
                        </h3>
                        <p class="mt-1 text-sm text-red-700 dark:text-red-300">
                            {{ classSubjectStore.error }}
                        </p>
                        <button
                            @click="classSubjectStore.clearError"
                            class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-500"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>

            <!-- Data Table -->
            <div
                v-if="!classSubjectStore.loading && !classSubjectStore.error"
                class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
            >
                <div
                    class="overflow-x-auto bg-gray-800 dark:bg-gray-900 shadow-md rounded-lg"
                >
                    <table
                        class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                        style="width: 100%"
                    >
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Class
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Subject
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Teacher
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Semester
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Schedule
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Room
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Status
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody
                            class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                        >
                            <tr
                                v-for="classSubject in classSubjectStore.classSubjects"
                                :key="classSubject.id"
                                class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div
                                        class="text-sm font-medium text-gray-900 dark:text-white"
                                    >
                                        {{
                                            classSubject.class?.grade_level ||
                                            "-"
                                        }}
                                        -
                                        {{
                                            classSubject.class?.name ||
                                            "Unknown"
                                        }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ classSubject.subject?.name || "-" }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{
                                        classSubject.teacher?.user?.name || "-"
                                    }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ classSubject.semester?.name || "-" }}
                                    /
                                    {{
                                        classSubject.semester?.academic_year
                                            ?.name ||
                                        classSubject.semester?.academic_year
                                            ?.year ||
                                        "No Academic Year"
                                    }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ classSubject.schedule || "-" }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ classSubject.room || "-" }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        v-if="classSubject.is_active"
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                    >
                                        Active
                                    </span>
                                    <span
                                        v-else
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                    >
                                        Inactive
                                    </span>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                                >
                                    <button
                                        @click="editClassSubject(classSubject)"
                                        title="Edit Class Subject"
                                        class="inline-flex items-center justify-center p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors mr-2"
                                    >
                                        <Pencil class="w-4 h-4" />
                                    </button>
                                    <button
                                        @click="confirmDelete(classSubject)"
                                        title="Delete Class Subject"
                                        class="inline-flex items-center justify-center p-2 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    >
                                        <Trash2 class="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Empty State -->
                <div
                    v-if="classSubjectStore.classSubjects.length === 0"
                    class="text-center py-12"
                >
                    <BookOpen class="mx-auto h-12 w-12 text-gray-400" />
                    <h3
                        class="mt-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                        No class subjects found
                    </h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Get started by adding a new class subject.
                    </p>
                    <div class="mt-6">
                        <button
                            @click="openModal"
                            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                            <Plus class="w-4 h-4 mr-1" />
                            Add Class Subject
                        </button>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div
                v-if="
                    (classSubjectStore.meta?.total || 0) >
                    (classSubjectStore.meta?.per_page || 10)
                "
                class="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6"
            >
                <div class="flex-1 flex justify-between sm:hidden">
                    <button
                        @click="previousPage"
                        :disabled="classSubjectStore.meta?.current_page === 1"
                        class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        Previous
                    </button>
                    <button
                        @click="nextPage"
                        :disabled="
                            classSubjectStore.meta?.current_page ===
                            classSubjectStore.meta?.last_page
                        "
                        class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        Next
                    </button>
                </div>
                <div
                    class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between"
                >
                    <div>
                        <p class="text-sm text-gray-700 dark:text-gray-300">
                            Showing
                            <span class="font-medium">{{
                                classSubjectStore.meta?.from ?? 0
                            }}</span>
                            to
                            <span class="font-medium">{{
                                classSubjectStore.meta?.to ?? 0
                            }}</span>
                            of
                            <span class="font-medium">{{
                                classSubjectStore.meta?.total ?? 0
                            }}</span>
                            results
                        </p>
                    </div>
                    <div>
                        <nav
                            class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                            aria-label="Pagination"
                        >
                            <button
                                @click="previousPage"
                                :disabled="
                                    classSubjectStore.meta?.current_page === 1
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                Previous
                            </button>
                            <button
                                v-for="page in visiblePages"
                                :key="page"
                                @click="
                                    typeof page === 'number'
                                        ? goToPage(page)
                                        : null
                                "
                                :class="[
                                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                    page ===
                                    classSubjectStore.meta?.current_page
                                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-blue-900 dark:text-blue-300'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300',
                                    typeof page === 'string'
                                        ? 'cursor-not-allowed'
                                        : 'cursor-pointer',
                                ]"
                                :disabled="typeof page === 'string'"
                            >
                                {{ page }}
                            </button>
                            <button
                                @click="nextPage"
                                :disabled="
                                    classSubjectStore.meta?.current_page ===
                                    classSubjectStore.meta?.last_page
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                Next
                            </button>
                        </nav>
                    </div>
                </div>
            </div>

            <!-- Class Subject Modal -->
            <ClassSubjectModal
                :showModal="showModal"
                :class-subject="selectedClassSubject"
                :is-editing="isEditing"
                @close="closeModal"
                @saved="handleSaved"
            />

            <!-- Delete Confirmation Modal -->
            <DeleteConfirmationModal
                v-if="showDeleteModal"
                :show="showDeleteModal"
                :title="deleteModalTitle"
                :message="deleteModalMessage"
                @confirm="handleDelete"
                @close="closeDeleteModal"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useClassSubjectStore } from "@/stores/classSubjectStore";
import SearchableSelect from "@/components/SearchableSelect.vue";
import StatusFilter from "@/components/StatusFilter.vue";
import PageLengthSelector from "@/components/PageLengthSelector.vue";
import ClassSubjectModal from "@/components/ClassSubjectModal.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import NotificationService from "@/utils/notifications";
import classSchoollApi, { type ClassSchooll } from "@/api/classSchooll";
import subjectApi, { type Subject } from "@/api/subject";
import teacherApi, { type Teacher } from "@/api/teacher";
import semesterApi, { type Semester } from "@/api/semester";
import type { ClassSubject } from "@/api/classSubject";
import { Plus, RotateCcw, Pencil, Trash2, BookOpen } from "@lucide/vue";

const classSubjectStore = useClassSubjectStore();
// State
const searchQuery = ref("");
const filters = ref({
    class_id: "",
    subject_id: "",
    teacher_id: "",
    semester_id: "",
    is_active: undefined as boolean | undefined,
});
const showModal = ref(false);
const showDeleteModal = ref(false);
const selectedClassSubject = ref<ClassSubject | null>(null);
const isEditing = ref(false);
const classSubjectToDelete = ref<ClassSubject | null>(null);
// Types for options with "Select All" option
type ClassOption = ClassSchooll | { id: ""; grade_level: string; name: string };
type SubjectOption = Subject | { id: ""; name: string; code: string };
type TeacherOption = Teacher | { id: ""; user: { name: string } };
type SemesterOption =
    | Semester
    | { id: ""; name: string; academic_year: { name: string } };

const classOptions = ref<ClassOption[]>([]);
const subjectOptions = ref<SubjectOption[]>([]);
const teacherOptions = ref<TeacherOption[]>([]);
const semesterOptions = ref<SemesterOption[]>([]);

// Delete modal properties
const deleteModalTitle = ref("Confirm Delete");
const deleteModalMessage = ref(
    "Are you sure you want to delete this class subject? This action cannot be undone.",
);

// Computed
const visiblePages = computed(() => {
    const current = classSubjectStore.meta?.current_page || 1;
    const last = classSubjectStore.meta?.last_page || 1;
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
        let i = Math.max(2, current - delta);
        i <= Math.min(last - 1, current + delta);
        i++
    ) {
        range.push(i);
    }

    if (current - delta > 2) {
        rangeWithDots.push(1, "...");
    } else {
        rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (current + delta < last - 1) {
        rangeWithDots.push("...", last);
    } else if (last > 1) {
        rangeWithDots.push(last);
    }

    return rangeWithDots;
});

const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.class_id !== "" ||
        filters.value.subject_id !== "" ||
        filters.value.teacher_id !== "" ||
        filters.value.semester_id !== "" ||
        filters.value.is_active !== undefined ||
        classSubjectStore.page !== 1 ||
        classSubjectStore.perPage !== 10
    );
});

// Methods
const handlePerPageChange = (value: number) => {
    classSubjectStore.perPage = value;
    classSubjectStore.page = 1;
    fetchClassSubjects();
};

const fetchClassSubjects = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
    };
    await classSubjectStore.fetchClassSubjects(searchFilters);
};

const fetchOptions = async () => {
    try {
        const [
            classesResponse,
            subjectsResponse,
            teachersResponse,
            semestersResponse,
        ] = await Promise.all([
            classSchoollApi.getAll({ per_page: 1000 }),
            subjectApi.getAll({ per_page: 1000 }),
            teacherApi.getAll({ per_page: 1000 }),
            semesterApi.getSemesters({ per_page: 1000 }),
        ]);
        // Add "Select All" option as the first element
        const classData: any = classesResponse.data;
        const subjectData: any = subjectsResponse.data;
        const teacherData: any = teachersResponse.data;
        const semesterData: any = semestersResponse.data;

        classOptions.value = [
            { id: "", grade_level: "", name: "All Classes" },
            ...(classData?.items || []),
        ];
        subjectOptions.value = [
            { id: "", name: "All Subjects", code: "" },
            ...(subjectData?.items || []),
        ];
        teacherOptions.value = [
            { id: "", user: { name: "All Teachers" } },
            ...(teacherData?.items || []),
        ];
        semesterOptions.value = [
            { id: "", name: "All Semesters", academic_year: { name: "" } },
            ...(semesterData?.items || []),
        ];
    } catch (error) {
        console.error("Failed to fetch options:", error);
    }
};

const getClassLabel = (classOption: ClassOption): string => {
    if (classOption.id === "") return classOption.name;
    return `${classOption.grade_level} - ${classOption.name}`;
};

const getSubjectLabel = (subjectOption: SubjectOption): string => {
    if (subjectOption.id === "") return subjectOption.name;
    return `${subjectOption.name} (${subjectOption.code})`;
};

const getTeacherName = (teacher: TeacherOption): string => {
    return teacher.user?.name || "All Teachers";
};

const getSemesterLabel = (semester: SemesterOption): string => {
    if (semester.id === "") return semester.name;
    return `${semester.name} - ${semester.academic_year?.name}`;
};

const debouncedSearch = useDebounce(fetchClassSubjects, 300);

const openModal = () => {
    selectedClassSubject.value = null;
    isEditing.value = false;
    showModal.value = true;
};

const editClassSubject = (classSubject: ClassSubject) => {
    selectedClassSubject.value = classSubject;
    isEditing.value = true;
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
    selectedClassSubject.value = null;
    isEditing.value = false;
};

const confirmDelete = (classSubject: ClassSubject) => {
    classSubjectToDelete.value = classSubject;
    deleteModalTitle.value = "Delete Class Subject";
    deleteModalMessage.value = `Are you sure you want to delete "${classSubject.display_name || classSubject.id}"? This action cannot be undone.`;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    classSubjectToDelete.value = null;
};

const handleSaved = () => {
    // Refresh class subjects list to show new/updated class subject
    fetchClassSubjects();
    showNotification(
        isEditing.value
            ? "Class subject updated successfully"
            : "Class subject created successfully",
        "success",
    );
    closeModal();
};

const handleDelete = async () => {
    try {
        if (classSubjectToDelete.value) {
            await classSubjectStore.deleteClassSubject(
                classSubjectToDelete.value.id,
            );
            showNotification("Class subject deleted successfully", "success");
            closeDeleteModal();
        }
    } catch (error) {
        showNotification("Failed to delete class subject", "error");
    }
};

const previousPage = () => {
    if ((classSubjectStore.meta?.current_page || 1) > 1) {
        classSubjectStore.setPage(
            (classSubjectStore.meta?.current_page || 1) - 1,
        );
        fetchClassSubjects();
    }
};

const nextPage = () => {
    if (
        (classSubjectStore.meta?.current_page || 1) <
        (classSubjectStore.meta?.last_page || 1)
    ) {
        classSubjectStore.setPage(
            (classSubjectStore.meta?.current_page || 1) + 1,
        );
        fetchClassSubjects();
    }
};

const goToPage = (page: number) => {
    classSubjectStore.setPage(page);
    fetchClassSubjects();
};

const resetFilters = async () => {
    searchQuery.value = "";
    filters.value.class_id = "";
    filters.value.subject_id = "";
    filters.value.teacher_id = "";
    filters.value.semester_id = "";
    filters.value.is_active = undefined;
    await classSubjectStore.resetFilters();
    fetchClassSubjects();
};

const showNotification = (message: string, type: "success" | "error") => {
    if (type === "success") {
        NotificationService.success(message);
    } else {
        NotificationService.error(message);
    }
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(() => {
    fetchClassSubjects();
    fetchOptions();
});
</script>
